// Categorías de eventos de conversión de Facebook, ordenadas por popularidad de uso real.
// Cada categoría agrupa eventos por etapa del embudo de marketing.

export const CATEGORY_POPULAR = 'popular';
export const CATEGORY_PURCHASES = 'purchases';
export const CATEGORY_LEADS = 'leads';
export const CATEGORY_ENGAGEMENT = 'engagement';
export const CATEGORY_SUBSCRIPTIONS = 'subscriptions';
export const CATEGORY_MOBILE_APP = 'mobile_app';
export const CATEGORY_OTHER = 'other';

// Los 5 eventos más usados en Facebook Ads — aparecen primero en la sección "Populares"
const POPULAR_ACTION_IDS = [
    'offsite_conversion.fb_pixel_purchase',
    'offsite_conversion.fb_pixel_lead',
    'onsite_conversion.messaging_conversation_started_7d',
    'onsite_conversion.lead_grouped',
    'offsite_conversion.fb_pixel_custom',
];
const POPULAR_ACTION_IDS_SET = new Set(POPULAR_ACTION_IDS);

// Mapeo de action_id → categoría. Ordenado por popularidad dentro de cada categoría.
const CATEGORY_ACTIONS = {
    [CATEGORY_PURCHASES]: [
        'offsite_conversion.fb_pixel_purchase',
        'omni_purchase',
        'onsite_web_purchase',
        'offsite_conversion.fb_pixel_view_content',
        'omni_view_content',
        'onsite_web_view_content',
        'offsite_conversion.fb_pixel_add_to_cart',
        'omni_add_to_cart',
        'onsite_web_add_to_cart',
        'offsite_conversion.fb_pixel_add_payment_info',
        'omni_add_payment_info',
        'offsite_conversion.fb_pixel_initiate_checkout',
        'omni_initiate_checkout',
        'onsite_web_app_initiate_checkout',
        'offsite_conversion.fb_pixel_search',
        'omni_search',
        'offsite_conversion.fb_pixel_add_to_wishlist',
        'omni_add_to_wishlist',
        'onsite_web_add_to_wishlist',
        'onsite_web_app_add_to_cart',
        'onsite_web_app_purchase',
        'onsite_web_app_view_content',
    ],
    [CATEGORY_LEADS]: [
        'onsite_conversion.messaging_conversation_started_7d',
        'lead_grouped',
        'offsite_conversion.fb_pixel_lead',
        'onsite_conversion.messaging_first_reply',
        'offsite_conversion.fb_pixel_complete_registration',
        'leadgen_grouped',
        'lead',
        'contact_total',
        'contact_website',
        'onsite_conversion.lead_grouped',
        'schedule_total',
        'schedule_website',
        'offsite_conversion.fb_pixel_submit_application',
        'submit_application_total',
        'onsite_conversion.click_to_call_call_confirm',
        'onsite_conversion.find_location_website',
        'find_location_total',
        'onsite_conversion.messaging_block',
        'onsite_conversion.flow_complete',
        'onsite_conversion.messaging_welcome_message_view',
    ],
    [CATEGORY_ENGAGEMENT]: [
        'link_click',
        'landing_page_view',
        'comment',
        'video_view',
        'post_engagement',
        'like',
        'post_reaction',
        'outbound_click',
        'photo_view',
        'post',
        'page_engagement',
        'post_save',
        'checkin',
        'rsvp',
    ],
    [CATEGORY_SUBSCRIPTIONS]: [
        'offsite_conversion.fb_pixel_subscribe',
        'omni_subscribe',
        'subscribe_total',
        'offsite_conversion.fb_pixel_start_trial',
        'omni_start_trial',
        'start_trial_total',
        'recurring_subscription_payment_total',
        'cancel_subscription_total',
    ],
    [CATEGORY_MOBILE_APP]: [
        'app_install',
        'app_use',
        'mobile_app_install',
        'omni_app_install',
        'app_custom_event',
        'app_custom_event.fb_mobile_purchase',
        'app_custom_event.fb_mobile_add_to_cart',
        'app_custom_event.fb_mobile_complete_registration',
        'app_custom_event.fb_mobile_initiated_checkout',
        'app_custom_event.fb_mobile_search',
        'app_custom_event.fb_mobile_content_view',
        'app_custom_event.fb_mobile_add_to_wishlist',
        'app_custom_event.fb_mobile_add_payment_info',
        'app_custom_event.fb_mobile_level_achieved',
        'app_custom_event.fb_mobile_activate_app',
        'app_custom_event.fb_mobile_achievement_unlocked',
        'app_custom_event.fb_mobile_rate',
        'app_custom_event.fb_mobile_spent_credits',
        'app_custom_event.fb_mobile_tutorial_completion',
        'app_custom_event.other',
        'onsite_conversion.ad_click_mobile_app',
        'onsite_conversion.ad_impression_mobile_app',
    ],
    [CATEGORY_OTHER]: [
        'offsite_conversion.fb_pixel_custom',
        'credit_spent',
        'games.plays',
        'offsite_conversion.fb_pixel_customize_product',
        'omni_custom',
        'customize_product_total',
        'offsite_conversion.fb_pixel_donate',
        'donate_total',
        'offsite_conversion.fb_pixel_find_location',
        'offsite_conversion.fb_pixel_schedule',
        'offsite_conversion.fb_pixel_contact',
    ],
};

// Prefijos que el backend agrega a los action_ids
const PREFIX_FIXED_ACTION = 'FIXED_ACTION_';
const PREFIX_CUSTOM_ACTION = 'CUSTOM_ACTION_';

/**
 * Quita el prefijo FIXED_ACTION_ o CUSTOM_ACTION_ de un action_id.
 */
export function stripActionPrefix(actionId) {
    if (typeof actionId !== 'string') return actionId;
    if (actionId.startsWith(PREFIX_FIXED_ACTION)) return actionId.slice(PREFIX_FIXED_ACTION.length);
    if (actionId.startsWith(PREFIX_CUSTOM_ACTION)) return actionId.slice(PREFIX_CUSTOM_ACTION.length);
    return actionId;
}

// Set con todos los action_ids categorizados para búsqueda rápida
const ALL_CATEGORIZED_IDS = new Set();
Object.values(CATEGORY_ACTIONS).forEach(ids => {
    ids.forEach(id => ALL_CATEGORIZED_IDS.add(id));
});

// Top N eventos más populares por categoría (para badge "popular")
const POPULAR_COUNT = 5;
const POPULAR_BY_CATEGORY = {};
Object.entries(CATEGORY_ACTIONS).forEach(([cat, ids]) => {
    POPULAR_BY_CATEGORY[cat] = new Set(ids.slice(0, POPULAR_COUNT));
});

// Orden de categorías para display
export const CATEGORY_ORDER = [
    CATEGORY_POPULAR,
    CATEGORY_PURCHASES,
    CATEGORY_LEADS,
    CATEGORY_ENGAGEMENT,
    CATEGORY_SUBSCRIPTIONS,
    CATEGORY_MOBILE_APP,
    CATEGORY_OTHER,
];

// i18n keys para nombres de categorías
export const CATEGORY_I18N_KEYS = {
    [CATEGORY_POPULAR]: 'categorized_actions.category_popular',
    [CATEGORY_PURCHASES]: 'categorized_actions.category_purchases',
    [CATEGORY_LEADS]: 'categorized_actions.category_leads',
    [CATEGORY_ENGAGEMENT]: 'categorized_actions.category_engagement',
    [CATEGORY_SUBSCRIPTIONS]: 'categorized_actions.category_subscriptions',
    [CATEGORY_MOBILE_APP]: 'categorized_actions.category_mobile_app',
    [CATEGORY_OTHER]: 'categorized_actions.category_other',
};

// Filtros de categoría: cada filtro define qué action_ids mostrar.
// Son filtros de VISTA (no seleccionan eventos, solo filtran cuáles se ven).
export const CATEGORY_FILTERS = [
    {
        id: 'ecommerce',
        i18nKey: 'categorized_actions.preset_ecommerce',
        filterActionIds: new Set(CATEGORY_ACTIONS[CATEGORY_PURCHASES]),
    },
    {
        id: 'leads_messaging',
        i18nKey: 'categorized_actions.preset_leads_messaging',
        filterActionIds: new Set([
            'onsite_conversion.messaging_conversation_started_7d',
            'onsite_conversion.messaging_first_reply',
            'lead_grouped',
            'onsite_conversion.lead_grouped',
            'contact_total',
            'contact_website',
            'onsite_conversion.messaging_block',
            'onsite_conversion.messaging_welcome_message_view',
        ]),
    },
    {
        id: 'leads_forms',
        i18nKey: 'categorized_actions.preset_leads_forms',
        filterActionIds: new Set([
            'offsite_conversion.fb_pixel_lead',
            'leadgen_grouped',
            'offsite_conversion.fb_pixel_complete_registration',
            'lead',
            'offsite_conversion.fb_pixel_submit_application',
            'submit_application_total',
            'onsite_conversion.click_to_call_call_confirm',
            'schedule_total',
            'schedule_website',
            'onsite_conversion.find_location_website',
            'find_location_total',
            'onsite_conversion.flow_complete',
        ]),
    },
    {
        id: 'engagement',
        i18nKey: 'categorized_actions.preset_engagement',
        filterActionIds: new Set(CATEGORY_ACTIONS[CATEGORY_ENGAGEMENT]),
    },
];

/**
 * Dado un action_id, devuelve la categoría correspondiente.
 * Si no está en ninguna categoría conocida, devuelve null.
 */
export function getCategoryForAction(actionId) {
    const bare = stripActionPrefix(actionId);
    for (const [category, ids] of Object.entries(CATEGORY_ACTIONS)) {
        if (ids.includes(bare)) {
            return category;
        }
    }
    return null;
}

/**
 * Devuelve el orden de popularidad de un action_id dentro de su categoría.
 * Menor número = más popular. -1 si no se encuentra.
 */
export function getPopularityOrder(actionId) {
    for (const ids of Object.values(CATEGORY_ACTIONS)) {
        const idx = ids.indexOf(actionId);
        if (idx !== -1) return idx;
    }
    return -1;
}

/**
 * Devuelve true si el action_id es "popular" dentro de su categoría (top N).
 */
export function isPopularAction(actionId, category) {
    const popular = POPULAR_BY_CATEGORY[category];
    return popular ? popular.has(stripActionPrefix(actionId)) : false;
}

/**
 * Agrupa una lista de items de acción en categorías.
 * Items custom (is_custom=true) van en una sección separada.
 * Items no categorizados van a CATEGORY_OTHER.
 *
 * @param {Array} items - [{action_id, description, name, status, is_custom, type}]
 * @param {Function} itemDataMap - función que mapea item a {name, id}
 * @returns {{ categorized: {[category]: Array}, custom: Array }}
 */
export function groupActionsByCategory(items, itemDataMap) {
    const categorized = {};
    CATEGORY_ORDER.forEach(cat => { categorized[cat] = []; });
    const custom = [];

    items.forEach(rawItem => {
        const mapped = itemDataMap(rawItem);
        const item = { ...mapped, raw: rawItem };

        if (rawItem.is_custom) {
            custom.push(item);
            return;
        }

        const bareId = stripActionPrefix(rawItem.action_id);

        // Copiar a Populares si corresponde (no se saca de su categoría original)
        if (POPULAR_ACTION_IDS_SET.has(bareId)) {
            categorized[CATEGORY_POPULAR].push(item);
        }

        const category = getCategoryForAction(rawItem.action_id);
        if (category) {
            categorized[category].push(item);
        } else {
            categorized[CATEGORY_OTHER].push(item);
        }
    });

    // Ordenar Populares según el orden definido en POPULAR_ACTION_IDS
    categorized[CATEGORY_POPULAR].sort((a, b) => {
        const idxA = POPULAR_ACTION_IDS.indexOf(stripActionPrefix(a.raw.action_id));
        const idxB = POPULAR_ACTION_IDS.indexOf(stripActionPrefix(b.raw.action_id));
        return idxA - idxB;
    });

    // Ordenar cada categoría por popularidad (orden del array CATEGORY_ACTIONS)
    CATEGORY_ORDER.filter(cat => cat !== CATEGORY_POPULAR).forEach(cat => {
        const order = CATEGORY_ACTIONS[cat] || [];
        categorized[cat].sort((a, b) => {
            const idxA = order.indexOf(stripActionPrefix(a.raw.action_id));
            const idxB = order.indexOf(stripActionPrefix(b.raw.action_id));
            // Los que no están en el orden predefinido van al final, alfabéticamente
            if (idxA === -1 && idxB === -1) return a.name.localeCompare(b.name);
            if (idxA === -1) return 1;
            if (idxB === -1) return -1;
            return idxA - idxB;
        });
    });

    // Custom va alfabético
    custom.sort((a, b) => a.name.localeCompare(b.name));

    return { categorized, custom };
}

// --- Traducciones de eventos de Facebook al español ---
// Basadas en la terminología oficial de Meta Ads Manager en español.
// El backend sigue enviando nombres en inglés; el frontend traduce al renderizar.

export const ACTION_TRANSLATIONS = {
    // Compras
    'offsite_conversion.fb_pixel_purchase': 'Compras del píxel',
    'omni_purchase': 'Compras agrupadas',
    'onsite_web_purchase': 'Compras en Facebook',
    'offsite_conversion.fb_pixel_view_content': 'Contenido visto del píxel',
    'omni_view_content': 'Contenido visto agrupado',
    'onsite_web_view_content': 'Contenido visto en Facebook',
    'offsite_conversion.fb_pixel_add_to_cart': 'Agregados al carrito del píxel',
    'omni_add_to_cart': 'Agregados al carrito agrupados',
    'onsite_web_add_to_cart': 'Agregados al carrito en Facebook',
    'offsite_conversion.fb_pixel_add_payment_info': 'Info de pago agregada del píxel',
    'omni_add_payment_info': 'Info de pago agregada agrupada',
    'offsite_conversion.fb_pixel_initiate_checkout': 'Inicios de pago del píxel',
    'omni_initiate_checkout': 'Inicios de pago agrupados',
    'onsite_web_app_initiate_checkout': 'Inicios de pago en Facebook',
    'offsite_conversion.fb_pixel_search': 'Búsquedas del píxel',
    'omni_search': 'Búsquedas agrupadas',
    'offsite_conversion.fb_pixel_add_to_wishlist': 'Agregados a lista de deseos del píxel',
    'omni_add_to_wishlist': 'Agregados a lista de deseos agrupados',
    'onsite_web_add_to_wishlist': 'Agregados a lista de deseos en Facebook',
    'onsite_web_app_add_to_cart': 'Agregados al carrito en app de Facebook',
    'onsite_web_app_purchase': 'Compras en app de Facebook',
    'onsite_web_app_view_content': 'Contenido visto en app de Facebook',

    // Leads y Contacto
    'onsite_conversion.messaging_conversation_started_7d': 'Conversaciones de mensajería iniciadas (7d)',
    'lead_grouped': 'Clientes potenciales agrupados (Messenger + Formularios)',
    'offsite_conversion.fb_pixel_lead': 'Clientes potenciales del píxel',
    'onsite_conversion.messaging_first_reply': 'Nuevas conversaciones de mensajería',
    'offsite_conversion.fb_pixel_complete_registration': 'Registros completados del píxel',
    'leadgen_grouped': 'Clientes potenciales de Facebook (Messenger + Formularios)',
    'lead': 'Todos los clientes potenciales agrupados',
    'contact_total': 'Contactos',
    'contact_website': 'Contactos del sitio web',
    'onsite_conversion.lead_grouped': 'Clientes potenciales en Facebook',
    'schedule_total': 'Citas programadas',
    'schedule_website': 'Citas programadas del sitio web',
    'offsite_conversion.fb_pixel_submit_application': 'Solicitudes enviadas del píxel',
    'submit_application_total': 'Solicitudes enviadas',
    'onsite_conversion.click_to_call_call_confirm': 'Clics de confirmación de llamada',
    'onsite_conversion.find_location_website': 'Búsquedas de ubicación del sitio web',
    'find_location_total': 'Búsquedas de ubicación',
    'onsite_conversion.messaging_block': 'Conversaciones de mensajería bloqueadas',
    'onsite_conversion.flow_complete': 'Flujos completados en Facebook',
    'onsite_conversion.messaging_welcome_message_view': 'Vistas del mensaje de bienvenida',

    // Engagement
    'link_click': 'Clics en el enlace',
    'landing_page_view': 'Vistas de la página de destino',
    'comment': 'Comentarios en publicaciones',
    'video_view': 'Reproducciones de video de 3 segundos',
    'post_engagement': 'Interacciones con la publicación',
    'like': 'Me gusta de la página',
    'post_reaction': 'Reacciones a la publicación',
    'outbound_click': 'Clics salientes',
    'photo_view': 'Vistas de fotos de la página',
    'post': 'Publicaciones compartidas',
    'page_engagement': 'Interacción con la página agrupada',
    'post_save': 'Publicaciones guardadas',
    'checkin': 'Check-ins',
    'rsvp': 'Respuestas a eventos',

    // Suscripciones
    'offsite_conversion.fb_pixel_subscribe': 'Suscripciones del píxel',
    'omni_subscribe': 'Suscripciones agrupadas',
    'subscribe_total': 'Suscripciones',
    'offsite_conversion.fb_pixel_start_trial': 'Pruebas iniciadas del píxel',
    'omni_start_trial': 'Pruebas iniciadas agrupadas',
    'start_trial_total': 'Pruebas iniciadas',
    'recurring_subscription_payment_total': 'Pagos de suscripción recurrente',
    'cancel_subscription_total': 'Suscripciones canceladas',

    // Mobile App
    'app_install': 'Instalaciones de la app',
    'app_use': 'Usos de la app',
    'mobile_app_install': 'Instalaciones de la app móvil',
    'omni_app_install': 'Instalaciones de la app agrupadas',
    'app_custom_event': 'Eventos personalizados de la app',
    'app_custom_event.fb_mobile_purchase': 'Compras en app móvil',
    'app_custom_event.fb_mobile_add_to_cart': 'Agregados al carrito en app móvil',
    'app_custom_event.fb_mobile_complete_registration': 'Registros en app móvil',
    'app_custom_event.fb_mobile_initiated_checkout': 'Inicios de pago en app móvil',
    'app_custom_event.fb_mobile_search': 'Búsquedas en app móvil',
    'app_custom_event.fb_mobile_content_view': 'Contenido visto en app móvil',
    'app_custom_event.fb_mobile_add_to_wishlist': 'Agregados a lista de deseos en app móvil',
    'app_custom_event.fb_mobile_add_payment_info': 'Info de pago en app móvil',
    'app_custom_event.fb_mobile_level_achieved': 'Niveles alcanzados en app móvil',
    'app_custom_event.fb_mobile_activate_app': 'Inicios de app móvil',
    'app_custom_event.fb_mobile_achievement_unlocked': 'Logros desbloqueados en app móvil',
    'app_custom_event.fb_mobile_rate': 'Calificaciones en app móvil',
    'app_custom_event.fb_mobile_spent_credits': 'Créditos gastados en app móvil',
    'app_custom_event.fb_mobile_tutorial_completion': 'Tutoriales completados en app móvil',
    'app_custom_event.other': 'Otras acciones de app móvil',
    'onsite_conversion.ad_click_mobile_app': 'Clics en anuncios in-app',
    'onsite_conversion.ad_impression_mobile_app': 'Impresiones de anuncios in-app',

    // Otros
    'offsite_conversion.fb_pixel_custom': 'Conversiones personalizadas del píxel',
    'credit_spent': 'Créditos gastados',
    'games.plays': 'Partidas de juegos',
    'offsite_conversion.fb_pixel_customize_product': 'Productos personalizados del píxel',
    'omni_custom': 'Eventos personalizados agrupados',
    'customize_product_total': 'Productos personalizados',
    'offsite_conversion.fb_pixel_donate': 'Donaciones del píxel',
    'donate_total': 'Donaciones',
    'offsite_conversion.fb_pixel_find_location': 'Búsquedas de ubicación del píxel',
    'offsite_conversion.fb_pixel_schedule': 'Citas programadas del píxel',
    'offsite_conversion.fb_pixel_contact': 'Contactos del píxel',
};

// Tooltips de ayuda para eventos que necesitan aclaración
export const ACTION_HELP_TOOLTIPS = {
    'onsite_conversion.messaging_conversation_started_7d':
        'Es el evento que se suele usar para medir las conversiones en WhatsApp y Messenger.',
};

export function getTranslatedName(actionId) {
    const bare = stripActionPrefix(actionId);
    return ACTION_TRANSLATIONS[bare] || null;
}

export function getHelpTooltip(actionId) {
    const bare = stripActionPrefix(actionId);
    return ACTION_HELP_TOOLTIPS[bare] || null;
}
